import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classNames from "classnames";
import * as immer from "immer";
import { Component } from "react";
import Sticky from "react-stickynode";
import { Alert, BusyIcon, Header, Icon, Input, ResizeButton, Spin, TreeNode } from "./components/index.js";
import { NonPeristableStorage, SUPPORTS_LOCAL_STORAGE, Storage, buildOrderedParentPairs, detectIE, getRecursivelyCheckedNodes, getTreeItemById, getTreeParentById, handleSearch, handleSearchBlur, handleSearchClose, handleSearchKeyDown, handleSortableTree, handleSortableTreeDidUpdate, updateTreeItemById, uuid } from "./util/index.js";
/**
 * The ReactJS All-in-One-Tree.
 *
 * @returns
 */ class Tree extends Component {
    static #_ = this.defaultProps = {
        theme: "default",
        style: {},
        attr: {},
        isSticky: false,
        isStickyHeader: false,
        isBusyHeader: false,
        treeStickyAttr: {},
        headerStickyAttr: {},
        isResizable: true,
        isFullWidth: false,
        defaultWidth: 250,
        minWidth: 250,
        maxWidth: 800,
        oppositeOffset: 16,
        // Toolbar
        isCreatableLinkDisabled: false,
        isCreatableLinkCancel: false,
        isToolbarActive: true,
        isToolbarBusy: false,
        headline: "Folders",
        renameSaveText: "Save",
        renameAddText: "Add",
        creatable: {
            buttons: {
                folder: {
                    icon: '<i class="fa fa-folder-open"></i>'
                }
            },
            backButton: {
                label: "Cancel"
            }
        },
        toolbar: {
            buttons: {
                rename: {
                    content: '<i class="fa fa-pencil"></i>'
                }
            },
            backButton: {
                label: "Cancel",
                save: "Done"
            }
        },
        multiToolbar: {
            buttons: {},
            backButton: {
                label: "Cancel"
            }
        },
        // Tree
        rootId: 0,
        sortableDelay: 100,
        thresholdPx: 5,
        noFoldersTitle: "No folders found",
        noFoldersDescription: "Click the above button to create a new folder.",
        noSearchResult: "No search results found",
        searchable: true,
        searchInputBusy: false,
        toggleExpandAll: true,
        isTreeLinkDisabled: false,
        isTreeBusy: false,
        isSortable: false,
        isSortableDisabled: false,
        isSortableBusy: false,
        forceSortableFallback: false,
        staticTree: [],
        tree: [],
        ignoreChildNodes: false,
        allowMultiSelect: false
    };
    /**
     * The constructor creates a local storage instance to save
     * the sidebar width and expand states (if supported and enabled).
     */ constructor(props){
        super(props);
        /**
     * The sidebar gets resized through the ResizeButton and chain it to
     * the onResize method.
     */ this.handleResize = (x, collapse)=>{
            this.state.collapsed !== collapse && this.setState({
                collapsed: collapse
            });
            this.props.onResize && this.props.onResize(x, collapse);
        };
        /**
     * The sidebar is resized successfully. Chain it to the onResizeFinished method
     * and save the width to the local storage if supported and enabled.
     */ this.handleResizeFinished = (width)=>{
            this.storage.setItem("width", width);
            width > 0 && this.storage.setItem("rwidth", width);
            this.props.onResizeFinished && this.props.onResizeFinished(width);
        };
        /**
     * A node gets expanded / collapsed. Chain it to the onNodeExpand method and
     * save the state to the local storage if supported and enabled.
     */ this.handleNodeExpand = (expanded, node)=>{
            const { onNodeExpand } = this.props;
            const { id } = node;
            id && this.storage.setItem(`expandNodes.${node.id}`, expanded);
            onNodeExpand && onNodeExpand(expanded, node);
            this.forceUpdate();
        };
        /**
     * A node gets checked / unchecked so we have to trigger an event (onCheck)
     * and rerender the complete tree so the toolbar gets rerendered, too. The node
     * itself do not rerender because they do not depend on the amount of checked items.
     */ this.handleCheck = (id)=>{
            const { onCheck } = this.props;
            onCheck && onCheck(id);
            this.forceUpdate();
        };
        /**
     * Toggle all expanded states.
     *
     * @method
     */ this.handleToggleAll = ()=>{
            const expanded = this.getExpandedNodes();
            const newExpandedNodes = {};
            const newState = !expanded.length;
            const fnChild = (param)=>{
                let { id, childNodes = [] } = param;
                if (childNodes.length) {
                    newExpandedNodes[id] = newState;
                    childNodes.forEach(fnChild);
                }
            };
            this.props.tree.forEach(fnChild);
            this.storage.setItem("expandNodes", newExpandedNodes);
            this.forceUpdate();
        };
        this.handleCloseMultiToolbar = ()=>{
            const { onCloseMultiToolbar } = this.props;
            onCloseMultiToolbar && onCloseMultiToolbar(getRecursivelyCheckedNodes(this.props.tree, null));
            this.forceUpdate();
        };
        /**
     * Render a tree.
     *
     * @param tree The tree
     * @param displayChildren If true the first nodes can have childNodes
     * @param createRoot Shows an additional node after the last one with an input field
     * @param context The tree context
     * @returns
     */ this.renderTree = (tree, displayChildren, createRoot, context)=>{
            if (displayChildren === void 0) displayChildren = true;
            if (createRoot === void 0) createRoot = undefined;
            if (context === void 0) context = "tree";
            const { renderItem, renderIcon, onRenameClose, onAddClose, onSelect, onNodePressF2, renameSaveText, renameAddText, ignoreChildNodes } = this.props;
            const nodeAttr = {
                renderItem,
                renderIcon,
                onRenameClose,
                onAddClose,
                onSelect,
                onNodePressF2,
                renameSaveText,
                renameAddText,
                ignoreChildNodes
            };
            const { isTreeLinkDisabled, rootId, allowMultiSelect } = this.props;
            const { resultSelectedNodeIdx } = this.state;
            const resultTreeLength = typeof resultSelectedNodeIdx === "number" && this.state.resultTree.length;
            const expandedState = this.storage.getItem("expandNodes") || {};
            const $checkable = context !== "static" && allowMultiSelect;
            const className = classNames({
                "aiot-disable-links": isTreeLinkDisabled,
                "aiot-sortable-one": tree.length === 1
            }, `aiot-context-${context}`);
            let i = -1;
            const anyNodeHasChildren = tree.some((n)=>n.childNodes && n.childNodes.length > 0);
            return [
                // "All" expander
                context === "tree" && anyNodeHasChildren && /*#__PURE__*/ _jsx("div", {
                    onClick: this.handleToggleAll,
                    className: classNames("aiot-expander", "aiot-expander-all", {
                        "aiot-open": this.getExpandedNodes(expandedState).length
                    })
                }, "all-expander"),
                /*#__PURE__*/ _jsxs("ul", {
                    className: className,
                    "data-childs-for": rootId,
                    ref: displayChildren ? this.handleSortableTree : undefined,
                    children: [
                        tree.map((node)=>{
                            i++;
                            const searchSelected = i % resultTreeLength === resultSelectedNodeIdx % resultTreeLength && !displayChildren;
                            const propSearchSelected = context === "search" ? searchSelected : undefined;
                            const createTreeNode = (_node, opts)=>/*#__PURE__*/ _jsx(TreeNode, {
                                    searchSelected: propSearchSelected,
                                    ..._node,
                                    onExpand: this.handleNodeExpand,
                                    onCheck: this.handleCheck,
                                    $checkable: $checkable,
                                    expandedState: expandedState,
                                    ...nodeAttr,
                                    onUlRef: displayChildren ? this.handleSortableTree : undefined,
                                    displayChildren: displayChildren && !ignoreChildNodes,
                                    ...opts
                                }, _node.id);
                            if (renderItem) {
                                return renderItem(createTreeNode, TreeNode, node);
                            } else {
                                return createTreeNode(node, null);
                            }
                        }),
                        !!createRoot && /*#__PURE__*/ _jsx(TreeNode, {
                            $_create: true,
                            renderIcon: renderIcon,
                            onRenameClose: onAddClose,
                            renameSaveText: this.props.renameAddText,
                            ...createRoot
                        })
                    ]
                }, "list-view")
            ];
        };
        /**
     * Render the tree wrapper. In this part the sticky component is ensured and
     * you do not have to worry about it.
     */ this.renderTreeWrapper = ()=>{
            const { isCreatableLinkCancel, createRoot, searchable, searchInputBusy, isTreeBusy, staticTree, tree, isSortableBusy, children, noFoldersTitle, noFoldersDescription, noSearchResult, autoFocusSearchInput } = this.props;
            const { sortingBusy, searchTerm, resultTree, resultTreeBusy } = this.state;
            return /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsxs("div", {
                    className: "aiot-nodes",
                    children: [
                        children,
                        staticTree && this.renderTree(staticTree, false, undefined, "static"),
                        staticTree && /*#__PURE__*/ _jsx("hr", {}),
                        searchable && /*#__PURE__*/ _jsx("div", {
                            className: "aiot-search",
                            children: /*#__PURE__*/ _jsx(Input, {
                                autoFocus: autoFocusSearchInput,
                                disabled: !tree.length || isCreatableLinkCancel || sortingBusy || isSortableBusy,
                                size: "small",
                                value: searchTerm,
                                onChange: this.handleSearch,
                                onBlur: this.handleSearchBlur,
                                onKeyDown: this.handleSearchKeyDown,
                                suffix: searchInputBusy || resultTreeBusy ? /*#__PURE__*/ _jsx(BusyIcon, {}) : searchTerm.length ? /*#__PURE__*/ _jsx(Icon, {
                                    type: "close",
                                    style: {
                                        cursor: "pointer"
                                    },
                                    onClick: this.handleSearchClose
                                }) : /*#__PURE__*/ _jsx(Icon, {
                                    type: "search"
                                })
                            })
                        }),
                        /*#__PURE__*/ _jsx(Spin, {
                            spinning: !!isTreeBusy || sortingBusy || isSortableBusy,
                            size: "small",
                            style: {
                                minHeight: 50
                            },
                            children: this.renderTree(resultTree || tree, !resultTree, resultTree ? undefined : createRoot, resultTree ? "search" : "tree")
                        }),
                        tree && !tree.filter((node)=>node.$visible).length && !isTreeBusy && /*#__PURE__*/ _jsx(Alert, {
                            message: noFoldersTitle,
                            description: noFoldersDescription,
                            type: "info",
                            showIcon: true
                        }),
                        resultTree && !resultTree.length && /*#__PURE__*/ _jsx(Alert, {
                            message: noSearchResult,
                            type: "warning",
                            showIcon: true
                        })
                    ]
                })
            });
        };
        /**
     * Render the main wrapper with sticky / resize functionality. It also
     * renders the Header with headline and toolbar.
     *
     * @method
     */ this.renderWrapper = (checkedNodes)=>{
            // Create resize styles
            const { props } = this;
            const { isResizable, opposite, minWidth, maxWidth, innerClassName, isSticky, isStickyHeader, isSortableBusy, headerStickyAttr, oppositeOffset, onResizeOpposite } = props;
            const { currentlySorting, sortingBusy, searchTerm, stickyTreeCalculatedTop, collapsed } = this.state;
            // Header
            const { headline, creatable, isCreatableLinkDisabled, isCreatableLinkCancel, isToolbarActive, isToolbarBusy, toolbar, multiToolbar, toolbarActiveButton, isBusyHeader } = props;
            const headerAttr = {
                headline,
                creatable,
                isCreatableLinkDisabled,
                isCreatableLinkCancel,
                isToolbarActive,
                isToolbarBusy,
                toolbar,
                multiToolbar,
                toolbarActiveButton,
                isBusyHeader
            };
            const bodyHeader = /*#__PURE__*/ _jsx(Header, {
                ...headerAttr,
                isToolbarActive: sortingBusy || isSortableBusy ? false : isToolbarActive,
                checkedNodes: checkedNodes,
                onCloseMultiToolbar: this.handleCloseMultiToolbar,
                isCreatableLinkDisabled: searchTerm || sortingBusy || isSortableBusy || checkedNodes.length ? true : props.isCreatableLinkDisabled
            });
            // Tree
            const bodyTreeWrapper = stickyTreeCalculatedTop !== undefined ? this.renderTreeWrapper() : undefined; // Avoid double rendering for massive tree's while waiting for componentDidMount
            const className = classNames("aiot-pad", innerClassName, {
                "aiot-currently-sorting": currentlySorting
            });
            // Create sticky attributes
            const treeStickyAttr = Object.assign({}, {
                top: stickyTreeCalculatedTop
            }, props.treeStickyAttr);
            // Create wrapper with toolbar and so on...
            return /*#__PURE__*/ _jsxs("div", {
                className: className,
                children: [
                    isResizable && opposite && /*#__PURE__*/ _jsx(ResizeButton, {
                        opposite: opposite,
                        minWidth: minWidth,
                        maxWidth: maxWidth,
                        initialWidth: this.storage.getItem("width"),
                        restoreWidth: this.storage.getItem("rwidth"),
                        containerId: this.id(),
                        onResize: this.handleResize,
                        onResizeOpposite: onResizeOpposite,
                        onResizeFinished: this.handleResizeFinished,
                        oppositeOffset: oppositeOffset
                    }),
                    !collapsed && (isStickyHeader ? // @ts-expect-error className is not in type but is respected
                    /*#__PURE__*/ _jsxs(Sticky, {
                        className: "aiot-fixed-header",
                        ...headerStickyAttr,
                        children: [
                            " ",
                            bodyHeader,
                            " "
                        ]
                    }) : /*#__PURE__*/ _jsxs("div", {
                        children: [
                            bodyHeader,
                            " "
                        ]
                    })),
                    !collapsed && (isSticky ? /*#__PURE__*/ _jsx(Sticky, {
                        ...treeStickyAttr,
                        children: bodyTreeWrapper
                    }) : /*#__PURE__*/ _jsxs("div", {
                        children: [
                            bodyTreeWrapper,
                            " "
                        ]
                    }))
                ]
            });
        };
        // State
        this.state = {
            uuid: uuid(),
            collapsed: false,
            stickyTreeCalculatedTop: undefined,
            currentlySorting: false,
            sortingBusy: false,
            // Search results
            searchTerm: "",
            resultSelectedNodeIdx: undefined,
            resultTreeBusy: false,
            resultTree: undefined
        };
        // Localstorage only when id given
        if (this.props.id && SUPPORTS_LOCAL_STORAGE) {
            this.storage = new Storage(this.id());
        } else {
            this.storage = new NonPeristableStorage();
        }
        this.handleSearch = handleSearch.bind(this);
        this.handleSearchBlur = handleSearchBlur.bind(this);
        this.handleSearchClose = handleSearchClose.bind(this);
        this.handleSearchKeyDown = handleSearchKeyDown.bind(this);
        this.handleSortableTree = handleSortableTree.bind(this);
        this.handleSortableTreeDidUpdate = handleSortableTreeDidUpdate.bind(this);
    }
    /**
     * When the component did mount initialize the sticky sidebar and header.
     */ componentDidMount() {
        // Calculate offset of tree if tree and header are sticky
        const { isSticky, isStickyHeader, treeStickyAttr, headerStickyAttr } = this.props;
        const obj = document.querySelector(`#${this.id()} .aiot-fixed-header > div`);
        let newTreeCalculatedTop = 0;
        if (isSticky && isStickyHeader && typeof treeStickyAttr.top === "undefined" && obj) {
            newTreeCalculatedTop = obj.offsetHeight;
            const headerStickyTop = headerStickyAttr.top;
            if (typeof headerStickyTop === "string") {
                const headerStickyTopObj = document.querySelector(headerStickyTop);
                newTreeCalculatedTop += headerStickyTopObj ? headerStickyTopObj.offsetHeight : 0;
            } else if (typeof headerStickyTop === "number") {
                newTreeCalculatedTop += headerStickyTop;
            }
        }
        // Set sticky tree calculated in state
        this.setState({
            stickyTreeCalculatedTop: newTreeCalculatedTop
        });
    }
    /**
     * When the component did update rehandle the sortable tree.
     */ componentDidUpdate(prevProps) {
        this.handleSortableTreeDidUpdate(prevProps);
    }
    /**
     * Get expanded tree nodes.
     *
     * @returns
     */ getExpandedNodes(expandedState) {
        if (expandedState === void 0) expandedState = this.storage.getItem("expandNodes") || {};
        return (this.props.tree || []).filter((param)=>{
            let { id, childNodes } = param;
            if (!childNodes || !childNodes.length) {
                return false;
            }
            return expandedState[id] === undefined ? true : expandedState[id];
        });
    }
    /**
     * Get an unique id for this container.
     *
     * @param append Append this string to the id with '--' suffix
     * @returns
     */ id(append) {
        const id = this.props.id || this.state.uuid;
        return append ? `${id}--${append}` : id;
    }
    /**
     * Render.
     */ render() {
        const { theme, attr, isFullWidth, toolbarActiveButton } = this.props;
        const checkedNodes = getRecursivelyCheckedNodes(this.props.tree, false);
        const className = classNames("aiot-tree", this.props.className, `aiot-theme-${theme}`, {
            "aiot-wrap-collapse": this.state.collapsed,
            "aiot-full-width": isFullWidth,
            "aiot-has-checked": checkedNodes.length > 0,
            "aiot-toolbar-active-button": toolbarActiveButton
        });
        const style = Object.assign({}, this.props.style, !isFullWidth && {
            width: `${this.props.defaultWidth}px`,
            minWidth: `${this.props.minWidth}px`,
            maxWidth: `${this.props.maxWidth}px`
        });
        // Create first wrapper attributes
        const wrapperAttr = {
            id: this.id(),
            style,
            ...attr,
            className,
            ref: (div)=>this.container = div
        };
        return /*#__PURE__*/ _jsxs("div", {
            ...wrapperAttr,
            children: [
                " ",
                this.renderWrapper(checkedNodes),
                " "
            ]
        });
    }
}
// Export
export { Tree };
export { uuid, updateTreeItemById, getTreeParentById, getTreeItemById, buildOrderedParentPairs, getRecursivelyCheckedNodes, detectIE, /**
     * @see https://github.com/mweststrate/immer
     */ immer };
export * from "./components/index.js";
